import { PXE_BACKEND_URL } from '@packages/shared-ui/constants';
import { createApi } from '@reduxjs/toolkit/query/react';
import type { GetUtilityRequest } from '@/clients/ecomm/utilitiesClient.types';
import type {
  BuildingInsightsRequest,
  BuildingInsightsResponse,
} from '@/clients/pxe-backend/buildingInsights';
import type { DataCenterFeatureCollection } from '@/clients/pxe-backend/dataCenters';
import type { FiveYearPriceChangeResult } from '@/clients/pxe-backend/eiaPriceChanges';
import type { LsesRequest, LsesResponse } from '@/clients/pxe-backend/lses';
import type {
  EligibilityRequest,
  EligibilityResponse,
} from '@/clients/pxe-backend/pxeBackendClient.types';
import type {
  TariffsRequest,
  TariffsResponse,
} from '@/clients/pxe-backend/tariffs';
import {
  BATTERY_PRODUCTS_PUBLIC_URL,
  ELIGIBILITY_URL,
} from '@/constants/baseUrls';
import type { PublicBatteryProductsResponse } from '@/types/models/battery/battery';
import type {
  NearbyCustomersRequest,
  NearbyCustomersResponse,
} from '@/types/models/battery/nearbyCustomers';
import type { SolarQuoteProducts } from '@/types/models/solarQuoteEcomm/solarProducts';
import getEnv, { type Environment } from '@/utils/environment';
import { axiosBaseQuery } from '../utils/axiosBaseQuery';

const env: Environment = getEnv();

export type SolarProductQueryParams = {
  state: string;
  utility: string;
  lat: number;
  long: number;
};

export type PublicBatteryProductsQueryParams = {
  state: string;
  utility: string;
};

export const pxeBackendApi = createApi({
  reducerPath: 'pxe-backend-api',
  baseQuery: axiosBaseQuery({
    baseUrl: PXE_BACKEND_URL[env],
  }),
  endpoints: (builder) => ({
    utility: builder.query<string[], GetUtilityRequest>({
      query: (body) => ({
        url: '/utility',
        method: 'POST',
        body,
      }),
    }),
    solarProducts: builder.query<SolarQuoteProducts, SolarProductQueryParams>({
      query: (queryParams) => ({
        url: '/products/solar',
        method: 'GET',
        params: queryParams,
      }),
    }),
    publicBatteryProducts: builder.query<
      PublicBatteryProductsResponse,
      PublicBatteryProductsQueryParams
    >({
      query: (queryParams) => ({
        url: BATTERY_PRODUCTS_PUBLIC_URL,
        method: 'GET',
        params: queryParams,
      }),
    }),
    retrofitEligibility: builder.query<EligibilityResponse, EligibilityRequest>(
      {
        query: (body) => ({
          url: ELIGIBILITY_URL,
          method: 'POST',
          body,
        }),
      }
    ),
    nearbyCustomers: builder.query<
      NearbyCustomersResponse,
      NearbyCustomersRequest
    >({
      query: (queryParams) => ({
        url: '/nearby-customers',
        method: 'GET',
        params: queryParams,
      }),
    }),
    rateOfChange: builder.query<FiveYearPriceChangeResult, void>({
      query: () => ({
        url: '/price-of-power/residential-price-changes/five-year',
        method: 'GET',
      }),
    }),
    dataCenters: builder.query<DataCenterFeatureCollection, void>({
      query: () => ({
        url: '/price-of-power/data-centers',
        method: 'GET',
      }),
    }),
    buildingInsights: builder.query<
      BuildingInsightsResponse,
      BuildingInsightsRequest
    >({
      query: ({ lat, long }) => ({
        url: '/price-of-power/building-insights',
        method: 'GET',
        params: { lat, long },
      }),
    }),
    lses: builder.query<LsesResponse, LsesRequest>({
      query: ({ zipCode }) => ({
        url: '/price-of-power/lses',
        method: 'GET',
        params: { zipCode },
      }),
    }),
    tariffs: builder.query<TariffsResponse, TariffsRequest>({
      query: ({ zipCode, lseId }) => ({
        url: '/price-of-power/tariffs',
        method: 'GET',
        params: { zipCode, lseId },
      }),
      keepUnusedDataFor: 86_400,
    }),
  }),
});

export const {
  useUtilityQuery,
  useSolarProductsQuery,
  usePublicBatteryProductsQuery,
  useLazyPublicBatteryProductsQuery,
  useRetrofitEligibilityQuery,
  useLazyRetrofitEligibilityQuery,
  useNearbyCustomersQuery,
  useRateOfChangeQuery,
  useDataCentersQuery,
  useBuildingInsightsQuery,
  useLsesQuery,
  useTariffsQuery,
  useLazyTariffsQuery,
} = pxeBackendApi;
